import { ProfileHandling } from "services";
import { Amplify } from "aws-amplify";
import { fetchAuthSession } from "aws-amplify/auth";
import outputs from "amplify_outputs.json";
import type { Schema } from "schema/data/resource";
import { generateClient } from "aws-amplify/data";

Amplify.configure(outputs);

const client = generateClient<Schema>({
  authMode: "userPool",
});

class GenuineStudentService {
  private async getAuthInfo() {
    const session = await fetchAuthSession();
    const identityId = session.identityId;
    const idToken = session.tokens?.idToken;
    const token = idToken?.toString();
    const userData = await ProfileHandling.getUserDataFromToken(idToken);
    const userDataWithId = { ...userData, identityId };
    return { idToken, token, userData: userDataWithId };
  }

  async getGenuineStudentDetails(id: string): Promise<any> {
    try {
      const { token } = await this.getAuthInfo();

      const { data, errors } = await client.models.GenuineStudent.get(
        {
          genuineStudentId: id,
        },
        {
          authMode: "userPool",
          authToken: token,
        }
      );

      if (errors) {
        console.error(
          `Error fetching genuine student details for ID ${id}:`,
          errors
        );
        throw new Error("Failed to fetch genuine student details");
      }

      return data;
    } catch (error) {
      console.error(
        `Error fetching genuine student details for ID ${id}:`,
        error
      );
      throw error;
    }
  }

  async updateGenuineStudentStatus(id: string): Promise<any> {
    try {
      const { token } = await this.getAuthInfo();

      const data = await client.models.GenuineStudent.update(
        {
          genuineStudentId: id,
        },
        {
          authMode: "userPool",
          authToken: token,
        }
      );

      return data;
    } catch (error) {
      console.error("Error updating genuine student:", error);
      throw new Error("Failed to update genuine student");
    }
  }

  async submitGenuineStudent(input: any): Promise<any> {
    try {
      const { token, userData } = await this.getAuthInfo();

      const data = await client.mutations.submitGenuineStudent(
        {
          input: {
            ...input,
            owner: userData.id,
            identityId: userData.identityId,
            email: userData.personalInformation.email,
          },
        },
        {
          authMode: "userPool",
          authToken: token,
        }
      );

      return data;
    } catch (error) {
      console.error("Error in genuine student process:", error);
      throw error;
    }
  }

  async updateGenuineStudent(
    genuineStudentId: string,
    updateFields: Partial<Schema["GenuineStudent"]["type"]>
  ): Promise<any> {
    try {
      const { token } = await this.getAuthInfo();

      const { data, errors } = await client.models.GenuineStudent.update(
        {
          genuineStudentId,
          ...updateFields,
        },
        {
          authMode: "userPool",
          authToken: token,
        }
      );

      if (errors) {
        console.error("Error updating genuine student:", errors);
        throw new Error("Failed to update genuine student");
      }

      return data;
    } catch (error) {
      console.error("Error updating genuine student:", error);
      throw error;
    }
  }

  async linkToApplication(
    genuineStudentId: string,
    applicationId: string
  ): Promise<any> {
    try {
      return await this.updateGenuineStudent(genuineStudentId, {
        applicationId,
      });
    } catch (error) {
      console.error(
        `Error linking GS ${genuineStudentId} to application ${applicationId}:`,
        error
      );
      throw error;
    }
  }
}

export const genuineStudentService = new GenuineStudentService();
